import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { displayBookingType } from '../../../constants/bookingTypes';
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { DateTime } from "luxon";
import Select from "react-select";
import EditBooking from "./EditBooking";
import jsPDF from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import LoadingPopup from "../../SharedView/LoadingPopup";
import ConfirmPopup from "../ConfirmPopup";
import InvoiceDrawer from "../InvoiceDrawer";
import "./summaryReport.css";

applyPlugin(jsPDF);

const VIEW_OPTIONS = [
  { value: "this_week", label: "This Calendar Week" },
  { value: "next_week", label: "Next Calendar Week" },
  { value: "last_week", label: "Last Calendar Week" },
  { value: "this_month", label: "This Calendar Month" },
  { value: "last_month", label: "Last Calendar Month" },
  { value: "custom", label: "Custom Dates" },
];

const toYMD = (dt) => dt.toFormat("yyyy-LL-dd");

function getDefaultRangeForView(v) {
  const now = DateTime.now().setZone("Asia/Dubai");
  if (v === "this_month") return { startDate: toYMD(now.startOf("month")), endDate: toYMD(now.endOf("month")) };
  if (v === "last_month") { const l = now.minus({ months: 1 }); return { startDate: toYMD(l.startOf("month")), endDate: toYMD(l.endOf("month")) }; }
  const sow = (d) => d.minus({ days: d.weekday - 1 }).startOf("day");
  const eow = (d) => sow(d).plus({ days: 6 }).endOf("day");
  if (v === "this_week") return { startDate: toYMD(sow(now)), endDate: toYMD(eow(now)) };
  if (v === "next_week") { const n = now.plus({ weeks: 1 }); return { startDate: toYMD(sow(n)), endDate: toYMD(eow(n)) }; }
  if (v === "last_week") { const l = now.minus({ weeks: 1 }); return { startDate: toYMD(sow(l)), endDate: toYMD(eow(l)) }; }
  return { startDate: "", endDate: "" };
}

const money = (n) => `AED ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDateShort = (iso) => {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" }).setZone("Asia/Dubai");
  return dt.isValid ? dt.toFormat("dd LLL, HH:mm") : "—";
};

// A booking that costs nothing needs no invoice (owner, 2026-09-15) — free
// touch-ups, waived cleanings — so it neither reads "Not Invoiced" nor
// counts as uninvoiced work.
const isFree = (b) => Math.abs(Number(b.total_with_vat || 0)) < 0.005;
const isUninvoiced = (b) => !b.zoho_invoice_id && !b.local_invoice_id && !isFree(b);

const SummaryReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const didInitRef = useRef(false);
  const userRole = localStorage.getItem("userRole");
  const isManager = userRole === "manager";

  const [viewOpt, setViewOpt] = useState(VIEW_OPTIONS[0]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [groupedData, setGroupedData] = useState({});
  const [modalData, setModalData] = useState(null);
  const [selectedBookings, setSelectedBookings] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Invoice drawer (2026-07-20): generating an invoice opens the shared
  // drawer (loading inside → details); clicking an "Invoiced · INV-…" badge
  // reopens it. Managers only, which is already the gate on this whole page.
  const [invoiceDrawerOpen, setInvoiceDrawerOpen] = useState(false);
  const [invoiceDrawerId, setInvoiceDrawerId] = useState(null);
  const [invoiceCreating, setInvoiceCreating] = useState(false);
  const [invoiceCreateError, setInvoiceCreateError] = useState(null);

  // Client filter
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  // Client whose report is currently loaded in `groupedData`. Used to disable
  // "Save as PDF" when the admin has changed the client selection but hasn't
  // pressed Apply yet — otherwise the PDF would export stale data.
  const [appliedClientId, setAppliedClientId] = useState(null);

  // Property filter (draft → applied on Apply, same as client view)
  const [properties, setProperties] = useState([]);
  const [draftPropertyOpts, setDraftPropertyOpts] = useState([]);
  const [appliedPropertyIds, setAppliedPropertyIds] = useState([]);

  // PDF
  const [includeLineItems, setIncludeLineItems] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  // Invoice delete confirmation — a two-step flow:
  //   1. 'confirm' — "Are you sure you want to delete?" Yes/Cancel.
  //   2. 'type'    — user must literally type DELETE before the button enables.
  // `deleteInvoiceTarget` holds the invoice being deleted across both stages;
  // `deleteInvoiceStage` picks which popup is visible.
  const [deleteInvoiceTarget, setDeleteInvoiceTarget] = useState(null); // { localInvoiceId, zohoNumber, bookingCount }
  const [deleteInvoiceStage, setDeleteInvoiceStage] = useState(null);   // null | 'confirm' | 'type'
  const [deleteInvoiceTyped, setDeleteInvoiceTyped] = useState('');
  const deleteInvoiceInputRef = useRef(null);

  const isCustom = viewOpt?.value === "custom";

  // Fetch clients + properties on mount
  useEffect(() => {
    (async () => {
      try { const res = await axios.get("/api/clients/active"); setClients(res.data); } catch (e) { console.error(e); }
    })();
    (async () => {
      try { const res = await axios.get("/api/properties"); setProperties(res.data); } catch (e) { console.error(e); }
    })();
  }, []);

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: c.id, label: c.display_name || `Client #${c.id}` })),
    [clients]
  );

  const propertyOptions = useMemo(() => {
    const filtered = selectedClient
      ? properties.filter((p) => p.client_id === selectedClient.value)
      : properties;
    return filtered.map((p) => ({
      value: p.id,
      label: `${p.address_line_1 || "—"} - Unit ${p.unit_number || "—"}`,
    }));
  }, [properties, selectedClient]);

  // Core fetch
  const fetchSummaryReport = useCallback(async (start, end, clientId) => {
    if (!start || !end) return;
    setLoading(true);
    try {
      try { await axios.post("/api/bookings/refresh-prices", { startDate: start, endDate: end }); } catch {}
      const params = { startDate: start, endDate: end };
      if (clientId) params.clientId = clientId;
      const { data } = await axios.get("/api/bookings/summary-report", { params });
      Object.values(data).forEach((cl) => {
        Object.values(cl.properties).forEach((pr) => {
          let sub = 0, vat = 0, tot = 0;
          pr.bookings.forEach((b) => { sub += Number(b.subtotal_amount || 0); vat += Number(b.vat_amount || 0); tot += Number(b.total_with_vat || 0); });
          pr.subtotal = sub; pr.vat = vat; pr.total = tot;
        });
      });
      setGroupedData(data);
      setAppliedClientId(clientId ?? null);
    } catch (error) { console.error("Error fetching summary report:", error); }
    finally { setLoading(false); }
  }, []);

  const syncUrl = useCallback((view, start, end, clientId) => {
    const p = new URLSearchParams();
    p.set("view", view); p.set("startDate", start); p.set("endDate", end);
    if (clientId) p.set("clientId", clientId);
    navigate(`?${p.toString()}`, { replace: true });
  }, [navigate]);

  // Init
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    const p = new URLSearchParams(location.search);
    const urlView = p.get("view"), urlStart = p.get("startDate"), urlEnd = p.get("endDate"), urlClient = p.get("clientId");
    if (urlStart && urlEnd) {
      const opt = VIEW_OPTIONS.find((o) => o.value === urlView) || VIEW_OPTIONS.find(o => o.value === "custom");
      setViewOpt(opt); setStartDate(urlStart); setEndDate(urlEnd);
      if (urlClient) setSelectedClient({ value: parseInt(urlClient, 10), label: "" });
      fetchSummaryReport(urlStart, urlEnd, urlClient || null);
      syncUrl(opt.value, urlStart, urlEnd, urlClient);
    } else {
      const dv = VIEW_OPTIONS[0];
      const { startDate: s, endDate: e } = getDefaultRangeForView(dv.value);
      setViewOpt(dv); setStartDate(s); setEndDate(e);
      fetchSummaryReport(s, e, null); syncUrl(dv.value, s, e, null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedClient && !selectedClient.label && clients.length > 0) {
      const m = clients.find((c) => c.id === selectedClient.value);
      if (m) setSelectedClient({ value: m.id, label: m.display_name || `Client #${m.id}` });
    }
  }, [clients, selectedClient]);

  useEffect(() => {
    if (!viewOpt?.value || viewOpt.value === "custom") return;
    const r = getDefaultRangeForView(viewOpt.value);
    setStartDate(r.startDate); setEndDate(r.endDate);
  }, [viewOpt?.value]);

  const handleApply = () => {
    if (!viewOpt?.value) return;
    setAppliedPropertyIds(draftPropertyOpts.map((o) => o.value));
    let s = startDate, e = endDate;
    if (viewOpt.value !== "custom") { const r = getDefaultRangeForView(viewOpt.value); s = r.startDate; e = r.endDate; }
    fetchSummaryReport(s, e, selectedClient?.value || null);
    syncUrl(viewOpt.value, s, e, selectedClient?.value);
  };

  // Filter grouped data by property
  const filteredData = useMemo(() => {
    if (!appliedPropertyIds.length) return groupedData;
    const allowed = new Set(appliedPropertyIds);
    const result = {};
    Object.entries(groupedData).forEach(([clientId, client]) => {
      const filteredProps = {};
      Object.entries(client.properties).forEach(([propId, prop]) => {
        if (allowed.has(parseInt(propId))) filteredProps[propId] = prop;
      });
      if (Object.keys(filteredProps).length > 0) {
        result[clientId] = { ...client, properties: filteredProps };
      }
    });
    return result;
  }, [groupedData, appliedPropertyIds]);

  // Multi-billing (PLAN_multi_billing.md): for a multi-billing client the
  // render group becomes client + billing entity — "the owner — Default", "the owner —
  // Marina View Holdings LLC" — default entity first, then labels A→Z.
  // Non-multi clients produce exactly one group with the plain client name,
  // so their rendering is byte-identical to before.
  const displayGroups = useMemo(() => {
    const groups = [];
    Object.entries(filteredData).forEach(([clientId, client]) => {
      if (!client.is_multi_billing) {
        groups.push({ key: clientId, clientId, name: client.client_name, properties: client.properties });
        return;
      }
      const byEntity = new Map(); // '' = default entity
      Object.entries(client.properties).forEach(([propId, prop]) => {
        const label = prop.property_info?.billing_entity_label || '';
        if (!byEntity.has(label)) byEntity.set(label, {});
        byEntity.get(label)[propId] = prop;
      });
      const labels = [...byEntity.keys()].sort((a, b) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)));
      labels.forEach((label) => {
        groups.push({
          key: `${clientId}::${label || '__default__'}`,
          clientId,
          name: `${client.client_name} — ${label || 'Default Billing'}`,
          properties: byEntity.get(label),
        });
      });
    });
    return groups;
  }, [filteredData]);

  const grandTotals = useMemo(() => {
    let bookings = 0, subtotal = 0, vat = 0, total = 0, uninvoiced = 0, uninvoicedTotal = 0;
    Object.values(filteredData).forEach((cl) => {
      Object.values(cl.properties).forEach((p) => {
        bookings += p.bookings.length;
        p.bookings.forEach((b) => {
          subtotal += Number(b.subtotal_amount || 0); vat += Number(b.vat_amount || 0); total += Number(b.total_with_vat || 0);
          if (isUninvoiced(b)) { uninvoiced += 1; uninvoicedTotal += Number(b.total_with_vat || 0); }
        });
      });
    });
    return { bookings, subtotal, vat, total, uninvoiced, uninvoicedTotal };
  }, [filteredData]);

  // Flat list for PDF — built from displayGroups so a multi-billing client's
  // properties come out contiguous per "Client — Entity" group; non-multi
  // clients keep one group per client, same as before.
  const flatProperties = useMemo(() => {
    const list = [];
    displayGroups.forEach((group) => {
      Object.entries(group.properties).forEach(([propId, prop]) => {
        list.push({ groupKey: group.key, groupName: group.name, clientId: group.clientId, propertyId: propId, property: prop });
      });
    });
    return list;
  }, [displayGroups]);

  // Booking items modal
  // Edit straight from the items modal (2026-08-03): the button opens
  // the full Edit Booking modal — which is also where price overrides are
  // made — so price changes are two clicks from the summary row.
  const [editBookingId, setEditBookingId] = useState(null);

  const fetchBookingItems = async (bookingId) => {
    try {
      const { data } = await axios.get(`/api/bookings/${bookingId}/items`);
      setModalData({ bookingId, fees: data });
    } catch (error) { console.error("Error fetching booking items:", error); }
  };

  // Selection
  const toggleBookingSelection = (id) => {
    setSelectedBookings((prev) => { const u = new Set(prev); if (u.has(id)) u.delete(id); else u.add(id); return u; });
  };
  const toggleClientSelection = (clientProperties) => {
    setSelectedBookings((prev) => {
      const u = new Set(prev);
      const all = Object.values(clientProperties).every((p) => p.bookings.every((b) => u.has(b.booking_id)));
      Object.values(clientProperties).forEach((p) => p.bookings.forEach((b) => { if (all) u.delete(b.booking_id); else u.add(b.booking_id); }));
      return u;
    });
  };
  const togglePropertySelection = (bookings) => {
    setSelectedBookings((prev) => {
      const u = new Set(prev);
      const all = bookings.every((b) => u.has(b.booking_id));
      bookings.forEach((b) => { if (all) u.delete(b.booking_id); else u.add(b.booking_id); });
      return u;
    });
  };

  const hasAlreadyInvoicedSelected = useCallback(() => {
    for (const cl of Object.values(filteredData))
      for (const pr of Object.values(cl.properties))
        for (const b of pr.bookings)
          if (selectedBookings.has(b.booking_id) && b.zoho_invoice_id) return true;
    return false;
  }, [filteredData, selectedBookings]);

  const handleGenerateInvoiceClick = () => {
    if (selectedBookings.size === 0) return;
    if (hasAlreadyInvoicedSelected()) setConfirmOpen(true);
    else generateInvoice(selectedBookings, () => setSelectedBookings(new Set()));
  };

  const requestDeleteInvoice = (booking) => {
    if (!booking.local_invoice_id) return;
    // Count how many bookings share this invoice across the current view
    let count = 0;
    Object.values(filteredData).forEach((cl) => {
      Object.values(cl.properties).forEach((p) => {
        p.bookings.forEach((b) => { if (b.local_invoice_id === booking.local_invoice_id) count += 1; });
      });
    });
    setDeleteInvoiceTarget({
      localInvoiceId: booking.local_invoice_id,
      zohoNumber: booking.zoho_invoice_number || null,
      bookingCount: count,
    });
    setDeleteInvoiceTyped('');
    setDeleteInvoiceStage('confirm');
  };

  const cancelDeleteInvoice = () => {
    setDeleteInvoiceStage(null);
    setDeleteInvoiceTarget(null);
    setDeleteInvoiceTyped('');
  };

  // Auto-focus the DELETE input when the second stage opens.
  useEffect(() => {
    if (deleteInvoiceStage === 'type') {
      // Defer one tick so the input has mounted before we call focus().
      const t = setTimeout(() => deleteInvoiceInputRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [deleteInvoiceStage]);

  const confirmDeleteInvoice = async (force = false) => {
    if (!deleteInvoiceTarget) return;
    const { localInvoiceId } = deleteInvoiceTarget;
    // Close the confirm dialog first so only the loading overlay shows
    setDeleteInvoiceStage(null);
    setDeleteInvoiceTarget(null);
    setDeleteInvoiceTyped('');
    setIsLoading(true);
    setFeedbackMessage("");
    try {
      const { data } = await axios.delete(`/api/invoices/${localInvoiceId}`, { data: { force } });
      setFeedbackMessage(`Invoice deleted. ${data.bookings_unlinked} booking${data.bookings_unlinked === 1 ? '' : 's'} unlinked.`);
      fetchSummaryReport(startDate, endDate, selectedClient?.value || null);
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || "Failed to delete invoice.";
      setFeedbackMessage(msg);
    } finally { setIsLoading(false); }
  };

  /**
   * Patch a subset of bookings in the already-loaded summary tree in place.
   * Avoids the heavy full refetch (`refresh-prices` + `summary-report`) when
   * the only thing that changed is the invoice linkage on specific bookings —
   * so staff can process multiple invoices back-to-back without the page
   * flashing and scroll resetting each time.
   */
  const patchBookingsInSummary = (bookingIds, patch) => {
    if (!bookingIds || bookingIds.length === 0) return;
    const targetSet = new Set(bookingIds.map(Number));
    setGroupedData((prev) => {
      let changed = false;
      const nextClients = {};
      for (const [clientId, cl] of Object.entries(prev)) {
        let clientTouched = false;
        const nextProps = {};
        for (const [propId, pr] of Object.entries(cl.properties || {})) {
          let propTouched = false;
          const nextBookings = pr.bookings.map((b) => {
            if (targetSet.has(Number(b.booking_id))) {
              propTouched = true;
              return { ...b, ...patch };
            }
            return b;
          });
          nextProps[propId] = propTouched ? { ...pr, bookings: nextBookings } : pr;
          if (propTouched) { clientTouched = true; changed = true; }
        }
        nextClients[clientId] = clientTouched ? { ...cl, properties: nextProps } : cl;
      }
      return changed ? nextClients : prev;
    });
  };

  const generateInvoice = async (sel, callback, force = false) => {
    if (sel.size === 0) return;
    // Open the drawer immediately in its "generating" state — loading lives
    // INSIDE the drawer, then it flips to the full invoice details.
    setInvoiceDrawerOpen(true);
    setInvoiceDrawerId(null);
    setInvoiceCreating(true);
    setInvoiceCreateError(null);
    try {
      const { data } = await axios.post("/api/invoices/", { booking_ids: Array.from(sel), force });
      const autoIncluded = data.auto_included_booking_ids || [];
      const localId = data.invoice.localInvoiceId;
      if (localId) {
        setInvoiceDrawerId(localId); // drawer fetches + shows the details
      } else {
        setInvoiceCreateError('The invoice was created but its id was not returned. Reopen it from the Invoices page.');
      }
      if (callback) callback();

      // Patch affected bookings in place instead of refetching the whole
      // summary tree. `effective_booking_ids` covers both the user's
      // selection and any bookings that were already on the invoice and
      // got refreshed (auto_included).
      const affectedIds =
        (data.effective_booking_ids && data.effective_booking_ids.length)
          ? data.effective_booking_ids
          : [...Array.from(sel), ...autoIncluded];
      patchBookingsInSummary(affectedIds, {
        local_invoice_id: data.invoice.localInvoiceId,
        zoho_invoice_id: data.invoice.zohoInvoiceId,
        zoho_invoice_number: data.invoice.zohoInvoiceNumber,
        invoice_status: data.invoice.status || 'draft', // fresh invoice = Draft (orange)
      });
    } catch (error) {
      console.error(error);
      const data = error.response?.data;
      // Multi-billing: MIXED_BILLING_ENTITIES (400) and BILLING_ENTITY_CHANGED
      // (409) carry a user-readable message — always show it verbatim, never
      // the generic failure line.
      if (data?.code === 'MIXED_BILLING_ENTITIES' || data?.code === 'BILLING_ENTITY_CHANGED') {
        setInvoiceCreateError(data.message);
      } else {
        setInvoiceCreateError(data?.message || "Failed to create the invoice. Please try again.");
      }
    } finally {
      setInvoiceCreating(false);
    }
  };

  // Re-open the drawer for an already-invoiced booking (manager taps the badge).
  const openInvoiceDrawer = (localInvoiceId) => {
    if (!localInvoiceId) return;
    setInvoiceCreating(false);
    setInvoiceCreateError(null);
    setInvoiceDrawerId(localInvoiceId);
    setInvoiceDrawerOpen(true);
  };

  const closeInvoiceDrawer = () => {
    setInvoiceDrawerOpen(false);
    setInvoiceDrawerId(null);
    setInvoiceCreating(false);
    setInvoiceCreateError(null);
  };

  // PDF generation (same as client view, adapted for staff)
  const generatePdf = async () => {
    if (!flatProperties.length) return;
    setPdfGenerating(true);
    try {
      let feesMap = {};
      if (includeLineItems) {
        const allIds = [];
        flatProperties.forEach(fp => (fp.property.bookings || []).forEach(b => allIds.push(b.booking_id)));
        const fetches = allIds.map(id => axios.get(`/api/bookings/${id}/items`).then(r => ({ id, data: r.data })).catch(() => ({ id, data: null })));
        const results = await Promise.all(fetches);
        results.forEach(r => { if (r.data) feesMap[r.id] = r.data; });
      }

      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.getWidth();
      const m = 14;
      let y = 20;

      doc.setFontSize(18); doc.setFont(undefined, 'bold');
      doc.text('Booking Summary Report', m, y); y += 8;
      doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(100);
      doc.text(`Period: ${startDate} to ${endDate}`, m, y); y += 5;
      doc.text(`Generated: ${DateTime.now().setZone('Asia/Dubai').toFormat('dd MMM yyyy, HH:mm')}`, m, y); y += 5;
      doc.text(`Bookings: ${grandTotals.bookings}  |  Subtotal: ${money(grandTotals.subtotal)}  |  VAT: ${money(grandTotals.vat)}  |  Total: ${money(grandTotals.total)}`, m, y); y += 10;
      doc.setTextColor(0);

      let lastGroup = null;
      for (const fp of flatProperties) {
        const bookings = fp.property.bookings || [];
        if (!bookings.length) continue;
        const propTotal = bookings.reduce((s, b) => s + Number(b.total_with_vat || 0), 0);

        // Group header — "Client" (non-multi) or "Client — Entity"
        // (multi-billing), one per contiguous display group.
        if (fp.groupKey !== lastGroup) {
          if (y > 250) { doc.addPage(); y = 20; }
          doc.setFontSize(13); doc.setFont(undefined, 'bold');
          doc.text(fp.groupName, m, y); y += 6;
          lastGroup = fp.groupKey;
        }

        if (y > 260) { doc.addPage(); y = 20; }
        doc.setFontSize(10); doc.setFont(undefined, 'bold');
        doc.text(`${fp.property.property_info.address_line_1} - Unit ${fp.property.property_info.unit_number}`, m, y);
        doc.setFontSize(8); doc.setFont(undefined, 'normal');
        doc.text(`${bookings.length} bookings  |  ${money(propTotal)}`, pw - m, y, { align: 'right' }); y += 5;

        if (!includeLineItems) {
          const head = [['Date', 'ID', 'Type', 'Status', 'Subtotal', 'VAT', 'Total']];
          const body = bookings.map(b => [
            fmtDateShort(b.booking_start), `#${b.booking_id}`,
            `${b.booking_type || '—'}${b.is_guest_invoice ? `\nGuest invoice${b.guest_name ? `: ${b.guest_name}` : ''}` : ''}`,
            b.booking_status || '—',
            money(b.subtotal_amount), money(b.vat_amount), money(b.total_with_vat),
          ]);
          doc.autoTable({
            startY: y, head, body, margin: { left: m, right: m },
            styles: { fontSize: 7.5, cellPadding: 2.5 },
            headStyles: { fillColor: [255, 90, 96], textColor: 255, fontStyle: 'bold', fontSize: 7 },
            columnStyles: { 0: { cellWidth: 32 }, 1: { cellWidth: 14 }, 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right', fontStyle: 'bold' } },
            theme: 'grid',
          });
          y = doc.lastAutoTable.finalY + 6;
        } else {
          for (const b of bookings) {
            const bDate = fmtDateShort(b.booking_start);
            const fees = feesMap[b.booking_id];
            if (y > 230) { doc.addPage(); y = 20; }
            doc.setFillColor(255, 90, 96);
            doc.rect(m, y, pw - m * 2, 7, 'F');
            doc.setFontSize(8); doc.setFont(undefined, 'bold'); doc.setTextColor(255);
            doc.text(`#${b.booking_id}   ${bDate}   ${b.booking_type || ''}   ${b.booking_status || ''}${b.is_guest_invoice ? `   GUEST INVOICE${b.guest_name ? ` — ${b.guest_name}` : ''}` : ''}`, m + 3, y + 5);
            doc.text(money(b.total_with_vat), pw - m - 3, y + 5, { align: 'right' });
            doc.setTextColor(0); y += 9;
            if (fees?.price_items?.length) {
              const hasDisc = fees.price_items.some(it => Number(it.discount || 0) > 0);
              const cols = hasDisc ? ['Item', 'Qty', 'Price', 'Disc.', 'Subtotal', 'VAT', 'Total'] : ['Item', 'Qty', 'Price', 'Subtotal', 'VAT', 'Total'];
              const rows = fees.price_items.map(it => {
                const qtyCell = it.computed_quantity != null
                    && Number(it.computed_quantity) !== Number(it.quantity)
                    ? `${String(it.computed_quantity).replace(/\.0+$/, '')} → ${String(it.quantity || 0).replace(/\.0+$/, '')}`
                    : String(it.quantity || 0).replace(/\.0+$/, '');
                const row = [it.item_name || it.description || 'Item', qtyCell, money(it.unit_price)];
                if (hasDisc) row.push(Number(it.discount || 0) > 0 ? `${it.discount}%` : '—');
                row.push(money(it.line_subtotal), money(it.tax_amount), money(it.line_total));
                return row;
              });
              const li = cols.length - 1;
              const cs = {};
              for (let i = 2; i <= li; i++) cs[i] = { halign: 'right' };
              cs[li] = { halign: 'right', fontStyle: 'bold' };
              doc.autoTable({ startY: y, head: [cols], body: rows, margin: { left: m, right: m }, styles: { fontSize: 7, cellPadding: 2, lineColor: [230, 230, 230], lineWidth: 0.3 }, headStyles: { fillColor: [248, 249, 250], textColor: [100, 100, 100], fontStyle: 'bold', fontSize: 6.5 }, columnStyles: cs, theme: 'grid' });
              y = doc.lastAutoTable.finalY;
            }
            doc.setFillColor(248, 249, 250); doc.rect(m, y, pw - m * 2, 6, 'F');
            doc.setDrawColor(230); doc.rect(m, y, pw - m * 2, 6, 'S');
            doc.setFontSize(7); doc.setFont(undefined, 'normal'); doc.setTextColor(100);
            doc.text(`Subtotal: ${money(b.subtotal_amount)}     VAT: ${money(b.vat_amount)}     Total: ${money(b.total_with_vat)}`, pw - m - 3, y + 4.2, { align: 'right' });
            doc.setTextColor(0); y += 10;
          }
          y += 2;
        }
      }

      if (y > 245) { doc.addPage(); y = 20; }
      doc.setDrawColor(200); doc.line(m, y, pw - m, y); y += 7;
      doc.setFontSize(10); doc.setFont(undefined, 'bold'); doc.text('Grand Total', m, y); y += 6;
      doc.setFont(undefined, 'normal'); doc.setFontSize(9);
      doc.text('Subtotal (excl. VAT):', m, y); doc.text(money(grandTotals.subtotal), pw - m, y, { align: 'right' }); y += 5;
      doc.text('VAT (5%):', m, y); doc.text(money(grandTotals.vat), pw - m, y, { align: 'right' }); y += 5;
      doc.setFont(undefined, 'bold'); doc.setFontSize(10);
      doc.text('Total (incl. VAT):', m, y); doc.text(money(grandTotals.total), pw - m, y, { align: 'right' });
      doc.save(`booking-summary-${startDate}-to-${endDate}.pdf`);
    } catch (err) { console.error(err); alert('Failed to generate PDF.'); }
    finally { setPdfGenerating(false); }
  };

  const hasData = Object.keys(filteredData).length > 0;

  return (
    <div className="sr">
      <div className="sr__header">
        <h1 className="sr__title">Bookings Summary Report</h1>
      </div>

      {/* Filters */}
      <div className="sr__filtersRow">
        <div className="sr__filter">
          <label className="sr__label">Report View</label>
          <Select classNamePrefix="sr-sel" value={viewOpt} onChange={setViewOpt} options={VIEW_OPTIONS} isSearchable={false} menuPortalTarget={document.body} menuPosition="fixed" />
        </div>
        <div className="sr__filter">
          <label className="sr__label">Client</label>
          <Select classNamePrefix="sr-sel" value={selectedClient} onChange={(v) => { setSelectedClient(v); setDraftPropertyOpts([]); }} options={clientOptions} isClearable isSearchable placeholder="All Clients" menuPortalTarget={document.body} menuPosition="fixed" />
        </div>
        <div className="sr__filter">
          <div className="sr__labelRow">
            <label className="sr__label">Properties</label>
            <button type="button" className="sr__miniLink" onClick={() => setDraftPropertyOpts([])} disabled={!draftPropertyOpts.length}>All</button>
          </div>
          <Select
            classNamePrefix="sr-sel"
            value={draftPropertyOpts}
            onChange={(v) => setDraftPropertyOpts(Array.isArray(v) ? v : [])}
            options={propertyOptions}
            isMulti closeMenuOnSelect={false} hideSelectedOptions isClearable isSearchable backspaceRemovesValue controlShouldRenderValue={false}
            placeholder={draftPropertyOpts.length ? `${draftPropertyOpts.length} selected` : "All properties"}
            menuPortalTarget={document.body} menuPosition="fixed"
          />
        </div>
        <div className="sr__filter">
          <label className="sr__label">Start Date</label>
          <input className="sr__input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} disabled={!isCustom} />
        </div>
        <div className="sr__filter">
          <label className="sr__label">End Date</label>
          <input className="sr__input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} disabled={!isCustom} />
        </div>
        <div className="sr__filter">
          <label className="sr__label sr__label--ghost">Apply</label>
          <button className="sr__btn sr__btnPrimary" onClick={handleApply} disabled={loading || (isCustom && (!startDate || !endDate))}>Apply</button>
        </div>

        {draftPropertyOpts.length > 0 && (
          <div className="sr__selectedProps">
            {draftPropertyOpts.map((o) => (
              <button key={o.value} type="button" className="sr__propPill" onClick={() => setDraftPropertyOpts(prev => prev.filter(x => x.value !== o.value))}>
                <span className="sr__propPillText">{o.label}</span>
                <span className="sr__propPillX">&times;</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="sr__inlineLoading">
          <div className="app-loading"><div className="app-spinner" /><div className="app-spinner-text">Loading summary...</div></div>
        </div>
      )}

      {/* Stats + actions */}
      {!loading && hasData && (
        <div className="sr__stats">
          <div className="sr__statCards">
            <div className="sr__statCard">
              <div className="sr__statLabel">Bookings</div>
              <div className="sr__statValue">{grandTotals.bookings}</div>
            </div>
            {isManager && (
              <>
                <div className="sr__statCard"><div className="sr__statLabel">Subtotal</div><div className="sr__statValue">{money(grandTotals.subtotal)}</div></div>
                <div className="sr__statCard"><div className="sr__statLabel">VAT</div><div className="sr__statValue">{money(grandTotals.vat)}</div></div>
                <div className="sr__statCard"><div className="sr__statLabel">Total (incl. VAT)</div><div className="sr__statValue">{money(grandTotals.total)}</div></div>
                <div
                  className={`sr__statCard ${grandTotals.uninvoiced ? 'sr__statCard--warn' : 'sr__statCard--ok'}`}
                  title="Bookings with a price that have no invoice yet. Free bookings are not counted."
                >
                  <div className="sr__statLabel">Uninvoiced</div>
                  <div className="sr__statValue">
                    {grandTotals.uninvoiced}
                    {grandTotals.uninvoiced > 0 && <span className="sr__statSub"> · {money(grandTotals.uninvoicedTotal)}</span>}
                  </div>
                </div>
              </>
            )}
          </div>
          {(isManager || selectedClient) && (
            (() => {
              // "Stale" = the selected client doesn't match the client whose
              // report is currently loaded. Forces the admin to press Apply
              // again before exporting — otherwise the PDF would contain the
              // previous client's data.
              const currentClientId = selectedClient?.value ?? null;
              const isStale = currentClientId !== appliedClientId;
              const pdfDisabled = pdfGenerating || loading || !hasData || isStale;
              return (
                <div className="sr__actions">
                  <label className="sr__checkLabel">
                    <input type="checkbox" checked={includeLineItems} onChange={(e) => setIncludeLineItems(e.target.checked)} />
                    <span>Include line items</span>
                  </label>
                  <button
                    className="sr__btn sr__btnGhost"
                    onClick={generatePdf}
                    disabled={pdfDisabled}
                    title={isStale ? 'Press Apply to load the report for the selected client first' : ''}
                  >
                    {pdfGenerating ? 'Generating...' : 'Save as PDF'}
                  </button>
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* Empty */}
      {!loading && !hasData && (
        <div className="sr__empty">
          <div className="sr__emptyTitle">No bookings found</div>
          <div className="sr__emptyText">Try a different period{appliedPropertyIds.length ? ", or clear the property filter" : ""} and press Apply.</div>
        </div>
      )}

      {/* Content — table-based layout */}
      {!loading && hasData && (
        <div className="sr__content">
          {displayGroups.map((group) => {
            const clientTotal = Object.values(group.properties).reduce((s, p) => s + (p.total || 0), 0);
            return (
              <div key={group.key} className="sr__clientCard">
                <div className="sr__clientTop">
                  <a href={`/clients/${group.clientId}`} className="sr__clientName">{group.name}</a>
                  <div className="sr__clientMeta">
                    {isManager && <span className="sr__metaPill sr__metaPillStrong">{money(clientTotal)}</span>}
                    {isManager && <input type="checkbox" className="sr__checkbox" onChange={() => toggleClientSelection(group.properties)} />}
                  </div>
                </div>

                {Object.entries(group.properties).map(([propertyId, property]) => (
                  <div key={propertyId} className="sr__propCard">
                    <div className="sr__propHeader">
                      <div className="sr__propHeaderLeft">
                        <a href={`/properties/${propertyId}`} className="sr__propName">{property.property_info.address_line_1} - Unit {property.property_info.unit_number}</a>
                      </div>
                      <div className="sr__propHeaderRight">
                        <span className="sr__propCount">{property.bookings.length} bookings</span>
                        {isManager && <span className="sr__propTotal">{money(property.total)}</span>}
                        {isManager && <input type="checkbox" className="sr__checkbox" onChange={() => togglePropertySelection(property.bookings)} />}
                      </div>
                    </div>

                    <div className="sr__bookingList">
                      {property.bookings.map((b) => (
                        <div key={b.booking_id} className="sr__bookingRow" onClick={() => fetchBookingItems(b.booking_id)}>
                          <div className="sr__bkLeft">
                            <span className="sr__bkDate">{fmtDateShort(b.booking_start)}</span>
                            <a href={`/bookings/${b.booking_id}`} className="sr__bkId" onClick={(e) => e.stopPropagation()}>#{b.booking_id}</a>
                          </div>
                          <div className="sr__bkMid">
                            <span className="sr__bkType">{displayBookingType(b.booking_type)}</span>
                            <span className={`sr__statusChip sr__statusChip--${b.booking_status.toLowerCase().replace(/\s+/g, '-')}`}>{b.booking_status}</span>
                            {/* Card and wallet are independent facts about a booking:
                                a card payment is its own receipt (card_paid_amount),
                                the wallet charge is separate (wallet_net_charged). The
                                backend hides the card round-trip so a fully-card booking
                                reports wallet_net_charged=0 and shows only the card chip;
                                a wallet booking shows only the wallet chip. Render both so
                                a hybrid shows both truthfully rather than masking one. */}
                            {/* A price that is not the list price says so
                                (2026-08-03) — the totals in this row are
                                already the discounted ones, this chip is the
                                why. */}
                            {b.has_price_override && (
                              <span className="sr__overrideBadge" title="A manager has overridden this booking's price">
                                Price override
                              </span>
                            )}
                            {Number(b.card_paid_amount || 0) > 0 && (
                              <span className="sr__cardBadge" title={`${money(b.card_paid_amount)} paid by card`}>
                                Card · {money(b.card_paid_amount)}
                              </span>
                            )}
                            {Number(b.wallet_net_charged || 0) > 0 && (
                              <span className="sr__walletBadge" title={`${money(b.wallet_net_charged)} charged from wallet`}>
                                Wallet · {money(b.wallet_net_charged)}
                              </span>
                            )}
                          </div>
                          <div className="sr__bkRight" onClick={(e) => e.stopPropagation()}>
                            {isManager && (
                              b.zoho_invoice_id || b.local_invoice_id ? (
                                <span className="sr__invoiceBadgeGroup">
                                  {/* Guest invoices bill a one-time customer, not the
                                      client's entities — say so right where the invoice
                                      number shows (2026-08-09). */}
                                  {b.is_guest_invoice && (
                                    <span
                                      className="sr__guestBadge"
                                      title="This invoice bills a one-time guest, not the client's billing entities"
                                    >
                                      Guest{b.guest_name ? ` · ${b.guest_name}` : ''}
                                    </span>
                                  )}
                                  {b.local_invoice_id ? (() => {
                                    // Orange Draft (not yet sent) · light-green Sent · solid-green
                                    // Paid (2026-07-20). A fresh/local invoice reads draft.
                                    const st = String(b.invoice_status || 'draft').toLowerCase();
                                    const meta = st === 'paid'
                                      ? { label: 'Paid', cls: 'sr__invoiceBadge--paid' }
                                      : st === 'draft'
                                        ? { label: 'Draft', cls: 'sr__invoiceBadge--draft' }
                                        : { label: 'Sent', cls: 'sr__invoiceBadge--yes' };
                                    return (
                                      <button
                                        type="button"
                                        className={`sr__invoiceBadge ${meta.cls}`}
                                        title="Open invoice details"
                                        onClick={(e) => { e.stopPropagation(); openInvoiceDrawer(b.local_invoice_id); }}
                                      >
                                        {meta.label}{b.zoho_invoice_number ? ` · ${b.zoho_invoice_number}` : ''}
                                      </button>
                                    );
                                  })() : (
                                    <span className="sr__invoiceBadge sr__invoiceBadge--draft">Draft (local only)</span>
                                  )}
                                  <button
                                    type="button"
                                    className="sr__invoiceDelBtn"
                                    title="Delete invoice"
                                    onClick={(e) => { e.stopPropagation(); requestDeleteInvoice(b); }}
                                  >
                                    <span className="sr__invoiceDelX">×</span>
                                    <span className="sr__invoiceDelLabel">Delete Invoice</span>
                                  </button>
                                </span>
                              ) : isFree(b) ? (
                                <span className="sr__invoiceBadge sr__invoiceBadge--free" title="AED 0.00 — nothing to invoice">No need invoice</span>
                              ) : (
                                <span className="sr__invoiceBadge sr__invoiceBadge--no">Not Invoiced</span>
                              )
                            )}
                            {isManager && <span className="sr__bkPrice">{money(b.total_with_vat)}</span>}
                            {isManager && (
                              <input type="checkbox" className="sr__checkbox" checked={selectedBookings.has(b.booking_id)} onChange={() => toggleBookingSelection(b.booking_id)} />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* FAB */}
      {isManager && selectedBookings.size > 0 && (
        <button className="sr__fab" onClick={handleGenerateInvoiceClick}>Generate Invoice ({selectedBookings.size})</button>
      )}

      {/* Fees modal */}
      {modalData && (() => {
        const fees = modalData.fees || {};
        const items = Array.isArray(fees.price_items) ? fees.price_items : [];
        const hasDiscount = items.some(it => Number(it.discount || 0) > 0);
        const hasAnyTax = items.some(it => Number(it.tax_amount || 0) > 0);
        return (
          <div className="sr__modalOverlay" onClick={() => setModalData(null)}>
            <div className="sr__modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-x" onClick={() => setModalData(null)} />
              <div className="sr__modalHead">
                <h3 className="sr__modalTitle"><a href={`/bookings/${modalData.bookingId}`} target="_blank" rel="noopener noreferrer">Booking #{modalData.bookingId}</a></h3>
                <button
                  type="button"
                  className="sr__modalEditBtn"
                  onClick={() => { setEditBookingId(modalData.bookingId); setModalData(null); }}
                  title="Open this booking in the edit form — price overrides live there too"
                >
                  ✎ Edit booking
                </button>
              </div>
              {items.length > 0 ? (
                <>
                  <div className="sr__modalTableWrap">
                    <table className="sr__modalTable">
                      <thead><tr>
                        <th>Item</th><th className="sr__right">Qty</th><th className="sr__right">Unit Price</th>
                        {hasDiscount && <th className="sr__right">Discount</th>}
                        <th className="sr__right">Subtotal</th><th className="sr__right">Tax</th><th className="sr__right">Line Total</th>
                      </tr></thead>
                      <tbody>
                        {items.map((it, idx) => (
                          <tr key={idx}>
                            <td>{it.item_name || it.description || "Item"}</td>
                            <td className="sr__right">
                                {/* A manually set quantity shows what it would
                                    have been too (2026-08-30). */}
                                {it.computed_quantity != null
                                  && Number(it.computed_quantity) !== Number(it.quantity) && (
                                    <s className="sr__was">
                                        {String(it.computed_quantity).replace(/\.0+$/, "")}
                                    </s>
                                )}
                                {String(it.quantity || 0).replace(/\.0+$/, "")}
                            </td>
                            <td className="sr__right">{money(it.unit_price)}</td>
                            {hasDiscount && <td className="sr__right">{Number(it.discount || 0) > 0 ? `${it.discount}%` : "—"}</td>}
                            <td className="sr__right">{money(it.line_subtotal)}</td>
                            <td className="sr__right">{hasAnyTax ? <>{money(it.tax_amount)} <span className="sr__taxRate">({Math.round(Number(it.tax_rate || 0) * 100)}%)</span></> : "—"}</td>
                            <td className="sr__right sr__bold">{money(it.line_total)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="sr__modalTotals">
                    <div className="sr__modalTotalRow"><span>Subtotal</span><span>{money(fees.subtotal_amount)}</span></div>
                    <div className="sr__modalTotalRow"><span>VAT (5%)</span><span>{money(fees.vat_amount)}</span></div>
                    <div className="sr__modalTotalRow sr__modalTotalRow--grand"><strong>Total (incl. VAT)</strong><span className="sr__bold">{money(fees.total_with_vat)}</span></div>
                  </div>
                </>
              ) : <div style={{ padding: "20px", textAlign: "center", color: "#94a3b8" }}>No fee breakdown found.</div>}
            </div>
          </div>
        );
      })()}

      <LoadingPopup isLoading={isLoading} feedbackMessage={feedbackMessage} onClose={() => setFeedbackMessage("")} />

      <InvoiceDrawer
        open={invoiceDrawerOpen}
        invoiceId={invoiceDrawerId}
        creating={invoiceCreating}
        createError={invoiceCreateError}
        canEdit={isManager}
        onRetryCreate={() => generateInvoice(selectedBookings, () => setSelectedBookings(new Set()))}
        onClose={closeInvoiceDrawer}
        onChanged={() => fetchSummaryReport(startDate, endDate, selectedClient?.value || null)}
        onDeleted={(bookingIds) => patchBookingsInSummary(bookingIds, {
          local_invoice_id: null, zoho_invoice_id: null, zoho_invoice_number: null, invoice_status: null,
        })}
        onSent={(bookingIds, status) => patchBookingsInSummary(bookingIds, { invoice_status: status || 'sent' })}
      />
      <ConfirmPopup isOpen={confirmOpen} title="Update Existing Invoice" message="One or more of these bookings already belongs to an invoice. The existing invoice will be updated with fresh prices (no duplicate created). If other bookings are also on that invoice, they'll be refreshed too to keep the invoice consistent. Continue?" confirmLabel="Update Invoice" cancelLabel="Cancel" isDestructive={false}
        onConfirm={() => { setConfirmOpen(false); generateInvoice(selectedBookings, () => setSelectedBookings(new Set())); }} onCancel={() => setConfirmOpen(false)} />

      {/* Step 1 — plain "Are you sure?" */}
      <ConfirmPopup
        isOpen={deleteInvoiceStage === 'confirm'}
        title="Delete Invoice"
        message={
          deleteInvoiceTarget
            ? `Are you sure you want to delete invoice ${deleteInvoiceTarget.zohoNumber || `#${deleteInvoiceTarget.localInvoiceId}`}? This will also delete it from Zoho Books (if it still exists there) and unlink ${deleteInvoiceTarget.bookingCount} booking${deleteInvoiceTarget.bookingCount === 1 ? '' : 's'}. This action cannot be undone.`
            : ''
        }
        confirmLabel="Yes, continue"
        cancelLabel="Cancel"
        isDestructive={true}
        onConfirm={() => {
          setDeleteInvoiceTyped('');
          setDeleteInvoiceStage('type');
        }}
        onCancel={cancelDeleteInvoice}
      />

      {/* Step 2 — user must literally type DELETE */}
      <ConfirmPopup
        isOpen={deleteInvoiceStage === 'type'}
        title="Type DELETE to confirm"
        message={
          <div>
            <p style={{ marginTop: 0 }}>
              This really can't be undone. To confirm deletion of invoice{' '}
              <strong>
                {deleteInvoiceTarget?.zohoNumber || `#${deleteInvoiceTarget?.localInvoiceId || ''}`}
              </strong>
              , type <strong>DELETE</strong> below.
            </p>
            <input
              ref={deleteInvoiceInputRef}
              type="text"
              value={deleteInvoiceTyped}
              onChange={(e) => setDeleteInvoiceTyped(e.target.value)}
              placeholder="Type DELETE"
              autoComplete="off"
              spellCheck={false}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: 14,
                border: '1px solid #ccc',
                borderRadius: 4,
                boxSizing: 'border-box',
                fontFamily: 'inherit',
                letterSpacing: 0.5,
              }}
            />
          </div>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDestructive={true}
        confirmDisabled={deleteInvoiceTyped !== 'DELETE'}
        onConfirm={() => confirmDeleteInvoice(false)}
        onCancel={cancelDeleteInvoice}
      />
      {editBookingId && (
        <EditBooking
          bookingId={editBookingId}
          onClose={() => { setEditBookingId(null); fetchSummaryReport(startDate, endDate, selectedClient?.value); }}
        />
      )}
    </div>
  );
};

export default SummaryReport;
